import React from 'react'
import {useTranslation} from 'react-i18next'

// The unified lifecycle across a pack's codes: an inactive (prerellenado) code
// that hasn't been handed to a traveler yet, one that has (but has no trip yet),
// one with a trip/tripbook already created, and one already printed.
export type CodeLifecycle = 'NOT_ASSIGNED' | 'DELIVERED' | 'TRIP_CREATED' | 'PRINTED'

export const LIFECYCLE_LABEL_KEY: Record<CodeLifecycle, string> = {
    NOT_ASSIGNED: 'packs.statusNotAssigned',
    DELIVERED: 'packs.statusDelivered',
    TRIP_CREATED: 'packs.statusTripCreated',
    PRINTED: 'packs.statusPrinted',
}

// Only "Entregado" is a status the agency can toggle manually today — "Viaje
// creado" and "Impreso" are system-driven, so those pills render inert.
export const LIFECYCLE_EDITABLE: CodeLifecycle[] = ['NOT_ASSIGNED', 'DELIVERED']

// Exact tints from the "Codigo unico" designs — a hairline border keeps the
// pale fills legible on the #FCFCFD section header/table surfaces.
const LIFECYCLE_STYLE: Record<CodeLifecycle, {bg: string; dot: string; border: string; text: string}> = {
    NOT_ASSIGNED: {bg: 'bg-[#F1F1F4]', dot: 'bg-[#9AA0A6]', border: 'border-black/[.06]', text: 'text-neutralBlack-900'},
    DELIVERED: {bg: 'bg-[#D8F3F6]', dot: 'bg-[#04A3B3]', border: 'border-[#04A3B3]/[.22]', text: 'text-[#046069]'},
    TRIP_CREATED: {bg: 'bg-[#FDF0CE]', dot: 'bg-[#E8A200]', border: 'border-black/[.06]', text: 'text-neutralBlack-900'},
    PRINTED: {bg: 'bg-[#D9F5E4]', dot: 'bg-[#18AE60]', border: 'border-black/[.06]', text: 'text-neutralBlack-900'},
}

export const CodeStatusPill: React.FC<{
    lifecycle: CodeLifecycle
    onClick?: (e: React.MouseEvent) => void
}> = ({lifecycle, onClick}) => {
    const {t} = useTranslation('dashboard')
    const style = LIFECYCLE_STYLE[lifecycle]
    return (
        <span
            onClick={onClick}
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs whitespace-nowrap ${style.bg} ${style.border} ${style.text} ${onClick ? 'cursor-pointer hover:brightness-95' : ''}`}
        >
            <span className={`w-[7px] h-[7px] rounded-full flex-none ${style.dot}`}/>
            {t(LIFECYCLE_LABEL_KEY[lifecycle])}
        </span>
    )
}
